import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import ReCAPTCHA from "react-google-recaptcha";

const PAYMENT_API = "https://msb-payment-api.vercel.app";
const RECAPTCHA_SITE_KEY = "6LfmsZoqAAAAAPQgbaeEuX7w8IZxz7Ncqgs2i0hY";

const MemberPaymentReturn = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const captchaRef = useRef(null);
  const [status, setStatus] = useState("loading"); // loading | success | unpaid | needsCaptcha | error
  const [message, setMessage] = useState("Confirming your payment...");
  const [reCaptcha, setReCaptcha] = useState("");
  const payload = {
    userId: searchParams.get("userId") || "",
    checkoutId: searchParams.get("checkoutId") || "",
  };

  const finalize = async ({ withCaptcha = false } = {}) => {
    const userId = (payload.userId || "").trim();
    const checkoutId = (payload.checkoutId || "").trim();

    if (!userId && !checkoutId) {
      setStatus("error");
      setMessage(
        "Missing payment reference. Please contact MSB Association support with your payment receipt."
      );
      return;
    }

    if (withCaptcha && !reCaptcha) {
      setStatus("needsCaptcha");
      setMessage("Please complete the reCAPTCHA to activate your membership in Backoffice.");
      return;
    }

    setStatus("loading");
    setMessage("Confirming your payment and activating membership...");

    try {
      const response = await axios.post(`${PAYMENT_API}/api/finalize-membership`, {
        userId: userId || undefined,
        checkoutId: checkoutId || undefined,
        reCaptcha: withCaptcha ? reCaptcha : undefined,
      });

      const data = response.data || {};

      if (data.paid && data.backofficeSynced) {
        setStatus("success");
        setMessage(data.message || "Payment confirmed. Membership activated.");
        toast.success("Membership activated successfully!");
        setTimeout(() => navigate("/member-success"), 1200);
        return;
      }

      if (data.paid && (data.needsCaptcha || !data.backofficeSynced)) {
        setStatus("needsCaptcha");
        setMessage(
          data.message ||
            "Payment confirmed. Please complete reCAPTCHA to finish activating membership."
        );
        setReCaptcha("");
        captchaRef.current?.reset?.();
        return;
      }

      setStatus("unpaid");
      setMessage(
        data.message ||
          "We could not confirm a successful payment yet. If you were charged, please wait a moment and retry, or contact support."
      );
    } catch (error) {
      const data = error.response?.data;
      if (data?.paid && (data?.needsCaptcha || !data?.backofficeSynced)) {
        setStatus("needsCaptcha");
        setMessage(
          data.message ||
            "Payment confirmed. Please complete reCAPTCHA to finish activating membership."
        );
        setReCaptcha("");
        captchaRef.current?.reset?.();
        return;
      }

      setStatus("error");
      setMessage(
        data?.message ||
          "Unable to confirm payment right now. Please retry, or contact MSB Association support."
      );
      console.error("Finalize membership error:", error);
    }
  };

  useEffect(() => {
    finalize({ withCaptcha: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className="section_space" style={{ minHeight: "60vh", paddingTop: "120px" }}>
      <div className="container" style={{ maxWidth: "640px" }}>
        <div
          className="text-center p-5"
          style={{
            background: "#f8f9fa",
            border: "1px solid #dee2e6",
            borderRadius: "15px",
          }}
        >
          <h2 className="mb-3">Membership Payment</h2>
          <p className="mb-4">{message}</p>

          {status === "loading" && (
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          )}

          {status === "needsCaptcha" && (
            <div className="d-flex flex-column gap-3 align-items-center">
              <ReCAPTCHA
                ref={captchaRef}
                sitekey={RECAPTCHA_SITE_KEY}
                onChange={(value) => setReCaptcha(value || "")}
              />
              <button
                type="button"
                className="btn btn-primary px-4"
                onClick={() => finalize({ withCaptcha: true })}
                disabled={!reCaptcha}
              >
                Activate membership
              </button>
            </div>
          )}

          {(status === "error" || status === "unpaid") && (
            <div className="d-flex flex-column gap-2 align-items-center">
              <button
                type="button"
                className="btn btn-primary px-4"
                onClick={() => finalize({ withCaptcha: false })}
              >
                Retry confirmation
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary px-4"
                onClick={() => navigate("/become-a-member#membership-form")}
              >
                Back to membership form
              </button>
            </div>
          )}

          {status === "success" && (
            <p className="text-success mb-0">Redirecting to confirmation page...</p>
          )}
        </div>
      </div>
    </section>
  );
};

export default MemberPaymentReturn;
